import { useEffect, useRef, useState } from "react";
import { SpeechRecognition, SpeechRecognitionEvent } from "../../../../interfaces/copilot-chat.interfaces";

export const useSpeechRecognition = (input: string) => {
    const [isRecording, setIsRecording] = useState(false);
    const [isSpeechSupported, setIsSpeechSupported] = useState(false);
    const recognitionRef = useRef<SpeechRecognition | null>(null);
    const [transcript, setTranscript] = useState("");
    const inputRef = useRef(input);

    // Update inputRef when input changes
    useEffect(() => {
        inputRef.current = input;
    }, [input]);

    useEffect(() => {
        const checkSpeechSupport = () => {
            const SpeechRecognitionClass =
                (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
            if (SpeechRecognitionClass) {
                setIsSpeechSupported(true);
                const recognition = new SpeechRecognitionClass();
                recognition.continuous = true;
                recognition.interimResults = true;
                recognition.lang = "en-US";
                recognitionRef.current = recognition;

                recognition.onstart = () => setIsRecording(true);
                recognition.onend = () => setIsRecording(false);
                recognition.onresult = (event: SpeechRecognitionEvent) => {
                    let interimTranscript = "";
                    let finalTranscript = inputRef.current;
                    for (let i = event.resultIndex; i < event.results.length; ++i) {
                        const result = event.results[i];
                        const text = result[0].transcript.trim();
                        if (result.isFinal) {
                            if (finalTranscript.length > 0 && !/[.!?]$/.test(finalTranscript.trim())) {
                                finalTranscript = finalTranscript.trim() + ". ";
                            }
                            finalTranscript += text + " ";
                        } else {
                            interimTranscript += text + " ";
                        }
                    }
                    setTranscript(finalTranscript + interimTranscript);
                };
                return true;
            } else {
                setIsSpeechSupported(false);
                return false;
            }
        };

        const speechSupported = checkSpeechSupport();

        if (!speechSupported) {
            const timeout = setTimeout(checkSpeechSupport, 500);
            return () => clearTimeout(timeout);
        }
    }, []);

    const startRecording = () => {
        if (recognitionRef.current && isSpeechSupported) {
            recognitionRef.current.start();
        }
    };

    const stopRecording = () => {
        if (recognitionRef.current && isSpeechSupported) {
            recognitionRef.current.stop();
        }
    };

    return {
        isRecording,
        isSpeechSupported,
        transcript,
        startRecording,
        stopRecording,
    };
};
